import React, { useMemo, useState } from "react";
// Mock Meter Events Component

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Stack from "@mui/material/Stack";

// Custom
import CustomDataTable from "../table/CustomTable";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";

const CHIP_COLORS = {
  good: "#7FCA59",
  pending: "#FDA81F",
  failed: "#F65B5B",
  high: "#F65B5B",
  medium: "#FDA81F",
  low: "#7FCA59",
  info: "#007AFF",
  closed: "#7FCA59",
  open: "#F65B5B",
  investigation: "#FDA81F",
};

const EVENT_CATEGORIES = [
  { label: "All Categories", value: "all" },
  { label: "Power Event", value: "power event" },
  { label: "Current Event", value: "current event" },
  { label: "Voltage Event", value: "voltage event" },
  { label: "Tamper", value: "tamper" },
  { label: "Control", value: "control" },
  { label: "Config Change", value: "config change" },
];

const EVENT_SEVERITIES = [
  { label: "All Severities", value: "all" },
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
];

const EVENT_STATUSES = [
  { label: "All Statuses", value: "all" },
  { label: "Open", value: "open" },
  { label: "Investigation", value: "investigation" },
  { label: "Closed", value: "closed" },
  { label: "Info", value: "info" },
];

const MockMeterEvents: React.FC = () => {
  const [filter, setFilter] = useState({
    status: "all",
    severity: "all",
    category: "all",
  });

  const COLUMNS = [
    "Time Stamp",
    "Event Name",
    "Category",
    "Status",
    "Severity",
    "Suggested Action",
  ];

  const getStatusChip = (type: string) => {
    return (
      <Box
        sx={{
          fontSize: "10px",
          fontWeight: 500,
          lineHeight: "17px",
          letterSpacing: "0.32px",
          padding: "3px 10px",
          color: "#ffffff",
          borderRadius: "3px",
          width: "100px",
          textAlign: "center",
          background:
            CHIP_COLORS[type.toLowerCase() as keyof typeof CHIP_COLORS],
        }}
      >
        {type.toUpperCase()}
      </Box>
    );
  };

  const rowsContentData = [
    {
      timeStamp: "2024-01-01 12:00:00",
      eventName: "Power failure – Occurrence",
      category: "Power Event",
      status: "Open",
      severity: "Low",
      suggestedAction: "Investigate usage",
    },
    {
      timeStamp: "2024-01-08 06:00:00",
      eventName: "Current reverse – Occurrence",
      category: "Current Event",
      status: "Open",
      severity: "Medium",
      suggestedAction: "Investigate usage",
    },
    {
      timeStamp: "2024-01-01 09:00:00",
      eventName: "Tampering – Magnetic influence",
      category: "Tamper",
      status: "Investigation",
      severity: "High",
      suggestedAction: "Physical inspection",
    },
    {
      timeStamp: "2024-01-01 12:00:00",
      eventName: "Load switch – Disconnected",
      category: "Control",
      status: "Closed",
      severity: "Low",
      suggestedAction: "Confirm disconnection",
    },
    {
      timeStamp: "2024-01-01 12:00:00",
      eventName: "Meter cover opened",
      category: "Tamper",
      status: "Open",
      severity: "Medium",
      suggestedAction: "On-site verification",
    },
    {
      timeStamp: "2024-01-01 12:00:00",
      eventName: "Overvoltage – Occurrence",
      category: "Voltage Event",
      status: "Open",
      severity: "Medium",
      suggestedAction: "Grid voltage check",
    },
    {
      timeStamp: "2024-01-01 12:00:00",
      eventName: "Load Limit Enabled",
      category: "Config Change",
      status: "info",
      severity: "Low",
      suggestedAction: "Logged for audit",
    },
  ];

  const filterContentData = useMemo(() => {
    return rowsContentData.filter((item) => {
      const statusMatch =
        filter.status === "all" ||
        item.status.toLowerCase() === filter.status.toLowerCase();

      const severityMatch =
        filter.severity === "all" ||
        item.severity.toLowerCase() === filter.severity.toLowerCase();

      const categoryMatch =
        filter.category === "all" ||
        item.category.toLowerCase() === filter.category.toLowerCase();

      return statusMatch && severityMatch && categoryMatch;
    });
  }, [rowsContentData, filter]);

  const rowsContent = filterContentData.map((item, index) => (
    <TableRow key={index}>
      <TableCell>{item.timeStamp}</TableCell>
      <TableCell>{item.eventName}</TableCell>
      <TableCell>{item.category}</TableCell>
      <TableCell>{getStatusChip(item.status)}</TableCell>
      <TableCell>{getStatusChip(item.severity)}</TableCell>
      <TableCell>{item.suggestedAction}</TableCell>
    </TableRow>
  ));

  return (
    <Card
      sx={{
        borderWidth: "1px",
        borderColor: "#E0E0E0",
        borderRadius: 2,
        m: 2,
      }}
    >
      <CardHeader title="Meter Events" />

      <CardContent sx={{ p: 0, borderTop: "1px solid #E0E0E0" }}>
        <Stack direction={"row"} gap={2} alignItems={"end"} p={2}>
          <CustomOutlinedSelect
            options={EVENT_STATUSES}
            value={filter.status}
            onChange={(newValue) =>
              setFilter({ ...filter, status: newValue.value.toString() })
            }
            label="Status:"
            placeholder="Status"
            width="200px"
            labelStyle={{
              color: "gray",
              marginBottom: "5px",
            }}
          />
          <CustomOutlinedSelect
            options={EVENT_SEVERITIES}
            value={filter.severity}
            onChange={(newValue) =>
              setFilter({ ...filter, severity: newValue.value.toString() })
            }
            label="Severity:"
            placeholder="Severity"
            width="200px"
            labelStyle={{
              color: "gray",
              marginBottom: "5px",
            }}
          />
          <CustomOutlinedSelect
            options={EVENT_CATEGORIES}
            value={filter.category}
            onChange={(newValue) =>
              setFilter({ ...filter, category: newValue.value.toString() })
            }
            label="Category:"
            placeholder="Category"
            width="200px"
            labelStyle={{
              color: "gray",
              marginBottom: "5px",
            }}
          />

          <ContainedButton
            label="Clear Filters"
            sx={{
              color: "#ffffff",
              height: "34px",
              borderRadius: "8px",
            }}
            onClick={() =>
              setFilter({
                status: "all",
                severity: "all",
                category: "all",
              })
            }
          />
        </Stack>

        <CustomDataTable
          columns={COLUMNS}
          rowsContent={rowsContent}
          isLoading={false}
          pagination={false}
          sxHeader={{ height: "40px" }}
        />
      </CardContent>
    </Card>
  );
};

export default MockMeterEvents;
